import { Check, ChevronDown, Pencil, Search, X } from 'lucide-react'
import * as React from 'react'
import { createPortal } from 'react-dom'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn, fuzzyIncludes } from '@/lib/utils'

const MAX_PANEL_HEIGHT = 340
const MIN_PANEL_HEIGHT = 180
const GAP = 6

export interface ComboboxOption {
  label: string
  value: string
}

interface ComboboxProps {
  value?: string
  onChange?: (value: string | undefined) => void
  onSearch?: (query: string) => void
  options: ComboboxOption[]
  placeholder?: string
  searchPlaceholder?: string
  loading?: boolean
  emptyText?: string
  disabled?: boolean
  searchable?: boolean
  clearable?: boolean
  invalid?: boolean
  className?: string
  /** Admin: edit (rename) a catalog option via a per-option pencil. */
  onEdit?: (option: ComboboxOption) => void
}

export function Combobox({
  value,
  onChange,
  onSearch,
  options,
  placeholder,
  searchPlaceholder,
  loading,
  emptyText = '—',
  disabled,
  searchable = true,
  clearable = false,
  invalid,
  className,
  onEdit
}: ComboboxProps) {
  const [open, setOpen] = React.useState(false)
  const [query, setQuery] = React.useState('')
  const [coords, setCoords] = React.useState<{
    top?: number
    bottom?: number
    left: number
    width: number
    maxHeight: number
  }>({ top: 0, left: 0, width: 0, maxHeight: MAX_PANEL_HEIGHT })
  const ref = React.useRef<HTMLDivElement>(null)
  const panelRef = React.useRef<HTMLDivElement>(null)
  const searchRef = React.useRef<HTMLInputElement>(null)

  const reposition = React.useCallback(() => {
    const el = ref.current
    if (!el) return
    const r = el.getBoundingClientRect()
    const spaceBelow = window.innerHeight - r.bottom - GAP - 8
    const spaceAbove = r.top - GAP - 8
    const openUp = spaceBelow < MIN_PANEL_HEIGHT && spaceAbove > spaceBelow
    const available = openUp ? spaceAbove : spaceBelow
    const maxHeight = Math.max(
      MIN_PANEL_HEIGHT,
      Math.min(MAX_PANEL_HEIGHT, available)
    )
    const width = Math.max(r.width, 220)
    const left = Math.max(8, Math.min(r.left, window.innerWidth - width - 8))
    if (openUp) {
      setCoords({
        bottom: window.innerHeight - r.top + GAP,
        left,
        width,
        maxHeight
      })
    } else {
      setCoords({ top: r.bottom + GAP, left, width, maxHeight })
    }
  }, [])

  React.useLayoutEffect(() => {
    if (!open) return
    reposition()
    // Focus without scrolling so the (fixed) panel stays aligned on first open.
    if (searchable) searchRef.current?.focus({ preventScroll: true })
  }, [open, reposition, searchable])

  React.useEffect(() => {
    if (!open) return
    const onScroll = () => reposition()
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node
      if (
        !ref.current?.contains(target) &&
        !panelRef.current?.contains(target)
      ) {
        setOpen(false)
      }
    }
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', onScroll)
    document.addEventListener('mousedown', onDown)
    return () => {
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', onScroll)
      document.removeEventListener('mousedown', onDown)
    }
  }, [open, reposition])

  const selected = options.find((o) => o.value === value)
  const filtered = onSearch
    ? options
    : options.filter((o) => fuzzyIncludes(o.label, query))

  return (
    <div ref={ref} className={cn('relative', className)}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'flex h-10 w-full items-center justify-between gap-2 rounded-2xl border bg-white px-3.5 text-left text-sm text-ink transition-colors hover:border-ink/30 focus:border-primary focus:outline-none disabled:cursor-not-allowed disabled:opacity-50',
          invalid ? 'border-destructive' : 'border-ink/[0.16]'
        )}
      >
        <span className={cn('truncate', !selected && 'text-muted-foreground')}>
          {selected?.label ?? placeholder}
        </span>
        {clearable && selected && !disabled ? (
          <span
            role="button"
            tabIndex={-1}
            aria-label="clear"
            onClick={(e) => {
              e.stopPropagation()
              onChange?.(undefined)
            }}
            className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-ink/[0.06] hover:text-ink"
          >
            <X className="h-3.5 w-3.5" />
          </span>
        ) : (
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
        )}
      </button>

      {open &&
        createPortal(
          <div
            ref={panelRef}
            data-floating-panel=""
            onPointerDown={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            style={{
              position: 'fixed',
              top: coords.top,
              bottom: coords.bottom,
              left: coords.left,
              width: coords.width,
              maxHeight: coords.maxHeight
            }}
            className="pointer-events-auto z-[1300] flex flex-col overflow-hidden rounded-2xl border border-ink/[0.08] bg-white shadow-[0_16px_40px_rgba(4,33,37,0.16)]"
          >
            {searchable && (
              <div className="relative shrink-0 border-b border-ink/[0.06] p-2">
                <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  ref={searchRef}
                  value={query}
                  placeholder={searchPlaceholder ?? placeholder}
                  onChange={(e) => {
                    setQuery(e.target.value)
                    onSearch?.(e.target.value)
                  }}
                  className="h-9 border-0 pl-9 shadow-none focus-visible:ring-0"
                />
              </div>
            )}
            <div className="min-h-0 flex-1 overflow-y-auto p-1">
              {loading ? (
                <div className="px-3 py-3 text-sm text-muted-foreground">…</div>
              ) : filtered.length === 0 ? (
                <div className="px-3 py-3 text-sm text-muted-foreground">
                  {emptyText}
                </div>
              ) : (
                filtered.map((o) => (
                  <div
                    key={o.value}
                    className={cn(
                      'flex items-center gap-1 rounded-xl pr-1 transition-colors hover:bg-ink/[0.04]',
                      o.value === value && 'text-primary'
                    )}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        onChange?.(o.value)
                        setOpen(false)
                      }}
                      className={cn(
                        'flex flex-1 items-center justify-between gap-2 border-0 bg-transparent px-3 py-2 text-left text-sm',
                        o.value === value
                          ? 'font-semibold text-primary'
                          : 'text-ink'
                      )}
                    >
                      <span className="truncate">{o.label}</span>
                      {o.value === value && (
                        <Check className="h-4 w-4 shrink-0" />
                      )}
                    </button>
                    {onEdit && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label="edit"
                        onClick={(e) => {
                          e.stopPropagation()
                          setOpen(false)
                          onEdit(o)
                        }}
                        className="h-7 w-7 rounded-full bg-transparent text-ink/40 hover:bg-ink/[0.06] hover:text-ink"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>,
          document.body
        )}
    </div>
  )
}
